import { defineStore } from 'pinia'
import api from '../api/http'
import {
  buildBrowserSuccessUrl,
  clearBrowserLoginState,
  loadBrowserLoginState,
  pollBrowserSession,
  saveBrowserLoginState,
} from '../auth/browser-session.mjs'
import { createAuthLifecycle, logoutLocally } from '../auth/auth-lifecycle.mjs'

let profileRequest = null
const lifecycle = createAuthLifecycle()

export const useAdminAuthStore = defineStore('adminAuth', {
  state: () => ({ user: null, profileVerified: false, authRevision: 0 }),
  getters: {
    isAuthenticated: (state) => state.profileVerified && !!state.user,
    isAdmin: (state) => ['admin', 'superuser'].includes(state.user?.role),
  },
  actions: {
    clearAuth() {
      lifecycle.invalidate()
      this.authRevision += 1
      this.user = null
      this.profileVerified = false
    },
    hydrateFromToken() {},
    async beginBrowserLogin(returnPath = '/admin') {
      lifecycle.invalidate()
      const revision = lifecycle.snapshot()
      await api.get('/auth/me/').catch(() => null)
      if (!lifecycle.isCurrent(revision)) return
      const createResponse = await api.post('/auth/browser-session/', {
        client_key: 'autosquare_resource_center',
        success_url: buildBrowserSuccessUrl(window.location),
      })
      if (!lifecycle.isCurrent(revision)) return
      const session = createResponse.data
      saveBrowserLoginState(window.sessionStorage, session, returnPath)
      window.location.replace(session.login_url)
    },
    async completeBrowserLogin() {
      const revision = lifecycle.snapshot()
      const session = loadBrowserLoginState(window.sessionStorage)
      if (!session) throw new Error('未找到待完成的登录会话，请重新登录。')
      let result
      try {
        result = await pollBrowserSession({ api, session })
      } catch (error) {
        if (error.sessionExpired) clearBrowserLoginState(window.sessionStorage)
        throw error
      }
      clearBrowserLoginState(window.sessionStorage)
      if (!lifecycle.isCurrent(revision)) return session.return_path
      if (!['admin', 'superuser'].includes(result.user?.role)) {
        this.clearAuth()
        const error = new Error('当前账号没有管理权限。')
        error.permissionDenied = true
        throw error
      }
      this.authRevision += 1
      lifecycle.invalidate()
      this.user = result.user
      this.profileVerified = true
      return session.return_path
    },
    fetchProfile() {
      if (profileRequest && lifecycle.isCurrent(profileRequest.revision)) return profileRequest.promise
      const pending = { revision: lifecycle.snapshot(), promise: null }
      profileRequest = pending
      const revision = pending.revision
      pending.promise = (async () => {
        try {
          const response = await api.get('/auth/me/')
          if (!lifecycle.isCurrent(revision)) return this.user
          if (!['admin', 'superuser'].includes(response.data.role)) {
            this.clearAuth()
            const error = new Error('权限不足，仅限管理员访问')
            error.permissionDenied = true
            throw error
          }
          this.user = response.data
          this.profileVerified = true
          return this.user
        } catch (error) {
          if (lifecycle.isCurrent(revision) && error.response?.status === 401) this.clearAuth()
          throw error
        } finally {
          if (profileRequest === pending) profileRequest = null
        }
      })()
      return pending.promise
    },
    async logout() {
      this.authRevision += 1
      await logoutLocally({
        lifecycle,
        request: () => api.post('/auth/logout/'),
        clear: () => this.clearAuth(),
        redirect: () => { window.location.href = '/login' },
      })
    },
    async switchAccount() {
      this.authRevision += 1
      await logoutLocally({
        lifecycle,
        request: () => api.post('/auth/logout/'),
        clear: () => this.clearAuth(),
        redirect: () => { window.location.href = `/login?return_to=${encodeURIComponent('/admin')}` },
      })
    },
  },
})
